"use client";

import { useEffect, useRef, useState } from "react";
import { AppDetails } from "src/types";

type AppServiceResponse = {
  slug: string;
  name: string;
  image: string;
  is_built_in?: boolean;
};

type SearchServicesResponse = {
  results: AppServiceResponse[];
};

/**
 * Fetches app details for multiple slugs in a single batch API request.
 * Uses /api/v4/services endpoint which supports fetching multiple apps at once.
 */
const fetchAppDetailsBatch = async (
  slugs: string[],
): Promise<Map<string, AppDetails>> => {
  if (slugs.length === 0) {
    return new Map();
  }

  const url = new URL("/api/v4/services", window.location.origin);
  url.searchParams.set("slugs", slugs.join(","));
  url.searchParams.set("limit", String(slugs.length));

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error("Failed to fetch app details");
  }

  const { results }: SearchServicesResponse = await response.json();

  return new Map(
    results.map((app) => [
      app.slug,
      {
        image: app.image,
        name: app.name,
        isBuiltIn: app.is_built_in ?? false,
      },
    ]),
  );
};

export const useAppDetailsBySlugs = ({
  slugs,
  skip = false,
}: {
  slugs: string[];
  skip?: boolean;
}) => {
  const shouldFetch = slugs.length > 0 && !skip;
  const [appDetailsMap, setAppDetailsMap] = useState<Map<string, AppDetails>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [error, setError] = useState<Error | null>(null);

  const prevSlugsRef = useRef<string[]>([]);

  useEffect(() => {
    if (skip) return;

    const areArraysEqual = (arr1: string[], arr2: string[]) => {
      if (arr1.length !== arr2.length) return false;

      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
      }

      return true;
    };

    if (areArraysEqual(prevSlugsRef.current, slugs)) {
      return; // Nothing to fetch
    }

    prevSlugsRef.current = slugs;

    const fetchAllAppDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const uniqueSlugs = Array.from(new Set(slugs));
        const appDetails = await fetchAppDetailsBatch(uniqueSlugs);
        setAppDetailsMap(appDetails);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllAppDetails();
  }, [slugs, skip]);

  return { data: appDetailsMap, isLoading, error };
};
